import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import SportHero, { SPORT_THEME } from './SportHero';

// The player carries hls.js, which is most of what this page would otherwise
// add to the bundle everyone downloads to watch a film. It loads on the first
// Watch instead.
const LivePlayer = lazy(() => import('./LivePlayer'));

/**
 * Live sport: what is on now, what is coming, and a Watch button that opens
 * the app's own player on it.
 *
 * Two sources are joined server-side (routes/live.js): ESPN for the fixture,
 * the score and the crests, and a stream aggregator for the mirrors. A row
 * ESPN does not know about still appears, with whatever name and badge the
 * aggregator gave it, so nothing with a stream is hidden.
 */

// The same tabs as routes/live.js, which owns the mapping to the stream
// aggregator's categories. All is what is on in every sport at once.
const SPORTS = [
  { id: 'all', label: 'All' },
  { id: 'soccer', label: 'Soccer' },
  { id: 'football', label: 'Football' },
  { id: 'basketball', label: 'Basketball' },
  { id: 'hockey', label: 'Hockey' },
  { id: 'baseball', label: 'Baseball' },
  { id: 'ufc', label: 'Fight' },
  { id: 'tennis', label: 'Tennis' },
  { id: 'motor', label: 'Motor' },
  { id: 'rugby', label: 'Rugby' },
  { id: 'cricket', label: 'Cricket' },
  { id: 'afl', label: 'AFL' },
  { id: 'golf', label: 'Golf' },
  { id: 'darts', label: 'Darts' },
  { id: 'other', label: 'Other' }
];

// What the server's warmer knows about a match's stream (services/live/warmer.js)
const READY_PILL = {
  ready: { text: 'Instant', title: 'A stream is already running: it starts as soon as you press Watch' },
  checking: { text: 'Checking', title: 'The server is starting a stream for this match now' },
  down: { text: 'No stream yet', title: 'No mirror would start a moment ago; pressing Watch tries them all again' }
};

// Scores and clocks move; the guide re-asks quietly at this cadence while the
// page is visible. The server caches for half of it, so two people watching
// cost the feeds one request.
const REFRESH_MS = 30_000;
const PREF_SPORT = 'live.sport';

const DAY = 24 * 60 * 60 * 1000;

const startOfDay = (ms) => {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const time = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** "Today", "Tomorrow", then the weekday - the way a TV guide labels its columns. */
const dayLabel = (ms, now) => {
  const today = startOfDay(now);
  const day = startOfDay(ms);
  if (day === today) return 'Today';
  if (day === today + DAY) return 'Tomorrow';
  if (day === today - DAY) return 'Yesterday';
  return new Date(ms).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' });
};

const kickoff = (ms, now) => (ms ? `${dayLabel(ms, now)} · ${time(ms)}` : 'Time to be confirmed');

/** "in 25 min" / "in 3 h" for the next thing on. */
const untilLabel = (ms, now) => {
  const minutes = Math.round((ms - now) / 60_000);
  if (minutes < 1) return 'starting now';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} h`;
  return `in ${Math.round(hours / 24)} d`;
};

const Crest = ({ src, name, size = 28 }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (src && !broken) {
    return <img className="live-crest" src={src} alt="" width={size} height={size} loading="lazy" onError={() => setBroken(true)} />;
  }
  return (
    <span className="live-crest live-crest--blank" style={{ width: size, height: size }}>
      {(name || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?'}
    </span>
  );
};

/** The status column: the clock during play, the kick-off before, the verdict after. */
const StatusLine = ({ item, now }) => {
  if (item.state === 'in') {
    const text = item.status.short || item.status.detail || 'Live';
    return (
      <span className="live-status live-status--on">
        <span className="live-dot" />
        {/^(live|in progress)$/i.test(text) ? 'LIVE' : text}
      </span>
    );
  }
  if (item.state === 'post') {
    return <span className="live-status live-status--off">{item.status.short || 'Finished'}</span>;
  }
  if (item.start) {
    const soon = item.start - now < 2 * 60 * 60 * 1000;
    return (
      <span className="live-status">
        {kickoff(item.start, now)}
        {soon && item.start > now ? <span className="live-status-soon">{untilLabel(item.start, now)}</span> : null}
      </span>
    );
  }
  return <span className="live-status">Time to be confirmed</span>;
};

const TeamRow = ({ team, showScore, winner, loser }) => (
  <div className={`live-team-row${loser ? ' is-loser' : ''}`}>
    <Crest src={team?.logo} name={team?.short || team?.name} />
    <span className="live-team-name">
      {team?.rank ? <span className="live-team-rank">{team.rank}</span> : null}
      {team?.name || '—'}
      {team?.record ? <span className="live-team-record">{team.record}</span> : null}
    </span>
    {showScore ? <span className={`live-team-score${winner ? ' is-winner' : ''}`}>{team?.score ?? '–'}</span> : null}
  </div>
);

const Bout = ({ bout, label }) => {
  if (!bout) return null;
  const [a, b] = bout.fighters;
  return (
    <div className="live-bout">
      <span className="live-bout-label">{label}</span>
      <span className="live-bout-names">
        {a?.name || '?'} <span className="live-vs">vs</span> {b?.name || '?'}
      </span>
      {bout.weightClass ? <span className="live-bout-weight">{bout.weightClass}</span> : null}
    </div>
  );
};

const Card = ({ item, now, onWatch }) => {
  const streams = item.streams?.length || 0;
  const live = item.state === 'in';
  const showScore = item.state !== 'pre' && item.home?.score != null;
  const homeWins = item.state === 'post' && item.home?.winner;
  const awayWins = item.state === 'post' && item.away?.winner;

  return (
    <div className={`live-card${live ? ' is-live' : ''}${item.state === 'post' ? ' is-over' : ''}`}>
      <div className="live-card-main">
        <div className="live-card-league">
          {item.league?.logo ? <img src={item.league.logo} alt="" className="live-league-logo" loading="lazy" /> : null}
          <span>
            {item.sportLabel && item.league?.name ? `${item.sportLabel} · ` : item.sportLabel || ''}
            {item.league?.name || (item.unlisted && !item.sportLabel ? 'Other' : '')}
          </span>
          {item.popular ? <span className="live-pill live-pill--hot">Popular</span> : null}
          {live && READY_PILL[item.ready] ? (
            <span className={`live-pill live-pill--${item.ready}`} title={READY_PILL[item.ready].title}>
              {READY_PILL[item.ready].text}
            </span>
          ) : null}
        </div>

        {item.kind === 'card' ? (
          <div className="live-fightcard">
            <div className="live-fightcard-name">{item.name}</div>
            {item.venue ? <div className="live-fightcard-venue">{item.venue}</div> : null}
            {item.segments?.length > 1 && item.state !== 'post' ? (
              <div className="live-fightcard-segments">
                {item.segments.map((segment) => (
                  <span key={segment.start} className={segment.start <= now ? 'is-on' : ''}>
                    {segment.name} {time(segment.start)}
                  </span>
                ))}
              </div>
            ) : null}
            <Bout bout={item.main} label="Main event" />
            {item.current ? <Bout bout={item.current} label="Now" /> : null}
            {item.state === 'in' && item.upcoming && item.upcoming !== item.main ? <Bout bout={item.upcoming} label="Next" /> : null}
          </div>
        ) : item.home || item.away ? (
          <div className="live-teams">
            <TeamRow team={item.home} showScore={showScore} winner={homeWins} loser={awayWins} />
            <TeamRow team={item.away} showScore={showScore} winner={awayWins} loser={homeWins} />
          </div>
        ) : (
          <div className="live-event-name">{item.name}</div>
        )}
      </div>

      <div className="live-card-side">
        <StatusLine item={item} now={now} />
        {streams ? (
          <button type="button" className={`btn btn--sm live-watch${live ? ' live-watch--on' : ''}`} onClick={() => onWatch(item)}>
            Watch
            <span className="live-watch-count">{streams}</span>
          </button>
        ) : (
          <span className="live-nostream">{item.state === 'post' ? 'No replay' : 'No stream yet'}</span>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, count, children, collapsible, open, onToggle }) => (
  <section className="live-section">
    <div className="live-section-head">
      <h2 className="live-section-title">
        {title}
        {count != null ? <span className="live-section-count">{count}</span> : null}
      </h2>
      {collapsible ? (
        <button type="button" className="live-section-toggle" onClick={onToggle}>
          {open ? 'Hide' : 'Show'}
        </button>
      ) : null}
    </div>
    {!collapsible || open ? children : null}
  </section>
);

const Live = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Sent here from the home hero: which sport to open on, and which match to
  // start playing as soon as the guide names it.
  const handoff = useRef(location.state || null);
  const [sport, setSport] = useState(() => {
    if (SPORTS.some((entry) => entry.id === handoff.current?.sport)) return handoff.current.sport;
    try {
      const saved = localStorage.getItem(PREF_SPORT);
      return SPORTS.some((entry) => entry.id === saved) ? saved : SPORTS[0].id;
    } catch (_) {
      return SPORTS[0].id;
    }
  });
  const tabsRef = useRef(null);

  // Fifteen tabs scroll sideways on a phone; keep the chosen one in view.
  // The strip is scrolled, not the tab: scrollIntoView also scrolls the page,
  // which tucks the strip under the fixed header.
  useEffect(() => {
    const strip = tabsRef.current;
    const active = strip?.querySelector('.tab.is-active');
    if (!strip || !active) return;
    const left = active.offsetLeft - strip.offsetLeft;
    if (left < strip.scrollLeft || left + active.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollLeft = Math.max(0, left - 24);
    }
  }, [sport]);
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [showFinished, setShowFinished] = useState(false);
  const [showLater, setShowLater] = useState(false);
  const [watching, setWatching] = useState(null);
  const guideFor = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(PREF_SPORT, sport);
    } catch (_) {
      // Private mode; nothing lost
    }
  }, [sport]);

  const load = useCallback(
    async (quiet) => {
      if (!quiet) {
        setLoading(true);
        setError('');
      }
      try {
        const { data } = await axios.get('/api/live/guide', { params: { sport } });
        guideFor.current = sport;
        setGuide(data.data);
        setNow(Date.now());
        setError('');
      } catch (err) {
        if (!quiet) setError(err?.response?.data?.error || err.message || 'Could not load the guide');
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [sport]
  );

  useEffect(() => {
    setShowLater(false);
    load(false);
  }, [load]);

  // Quiet refresh while the tab is visible - a phone left on the guide in a
  // pocket does not need to poll
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const items = useMemo(() => (guide && guideFor.current === sport ? guide.items : []), [guide, sport]);

  // The match the hero pressed Watch on: start it the moment the guide lists
  // it, then spend the handoff so Back never restarts it.
  useEffect(() => {
    const wanted = handoff.current?.watch;
    if (!wanted || !items.length) return;
    handoff.current = null;
    const item = items.find((entry) => entry.id === wanted);
    if (item) setWatching(item);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [items, navigate, location.pathname, location.search]);
  const counts = guide?.counts || {};

  const live = items.filter((item) => item.state === 'in');
  const upcoming = items.filter((item) => item.state === 'pre');
  const finished = items.filter((item) => item.state === 'post');

  // Upcoming is grouped by day so "Tomorrow" is one glance, not a scroll
  const days = useMemo(() => {
    const groups = [];
    for (const item of upcoming) {
      const label = item.start ? dayLabel(item.start, now) : 'Time to be confirmed';
      let group = groups[groups.length - 1];
      if (!group || group.label !== label) {
        group = { label, items: [] };
        groups.push(group);
      }
      group.items.push(item);
    }
    return groups;
  }, [upcoming, now]);

  const visibleDays = showLater ? days : days.slice(0, 2);
  const hiddenLater = days.slice(2).reduce((sum, group) => sum + group.items.length, 0);

  // The overlay keeps showing the score while it plays
  const watchingItem = watching ? items.find((item) => item.id === watching.id) || watching : null;

  return (
    <div className="page sports-page" style={{ '--hue': SPORT_THEME[sport]?.hue ?? 145 }}>
      <div className="tabs sport-tabs" ref={tabsRef}>
        {SPORTS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`tab${sport === entry.id ? ' is-active' : ''}`}
            style={{ '--hue': SPORT_THEME[entry.id]?.hue ?? 145 }}
            onClick={() => setSport(entry.id)}
          >
            {entry.label}
            {counts[entry.id] ? <span className="live-tab-live">{counts[entry.id]}</span> : null}
          </button>
        ))}
        <span className="sport-tabs-note">
          {guide?.generatedAt ? `Scores as of ${time(guide.generatedAt)}` : ''}
        </span>
      </div>

      <SportHero sport={sport} live={live.length} next={upcoming[0]?.start ? untilLabel(upcoming[0].start, now) : null} />

      {error && <div className="alert">{error}</div>}

      {loading && !items.length ? (
        <div className="loading">LOADING</div>
      ) : (
        <>
          <Section title="Live now" count={live.length}>
            {live.length ? (
              <div className="live-list">
                {live.map((item) => (
                  <Card key={item.id} item={item} now={now} onWatch={setWatching} />
                ))}
              </div>
            ) : (
              <div className="live-quiet">
                Nothing live right now.
                {upcoming[0]?.start ? ` Next up ${untilLabel(upcoming[0].start, now)}.` : ''}
              </div>
            )}
          </Section>

          {visibleDays.map((group) => (
            <Section key={group.label} title={group.label} count={group.items.length}>
              <div className="live-list">
                {group.items.map((item) => (
                  <Card key={item.id} item={item} now={now} onWatch={setWatching} />
                ))}
              </div>
            </Section>
          ))}

          {!showLater && hiddenLater > 0 ? (
            <button type="button" className="btn btn--soft btn--sm live-more" onClick={() => setShowLater(true)}>
              Show {hiddenLater} more later this week
            </button>
          ) : null}

          {finished.length ? (
            <Section
              title="Finished"
              count={finished.length}
              collapsible
              open={showFinished}
              onToggle={() => setShowFinished((open) => !open)}
            >
              <div className="live-list">
                {finished.map((item) => (
                  <Card key={item.id} item={item} now={now} onWatch={setWatching} />
                ))}
              </div>
            </Section>
          ) : null}

          {!items.length && !loading ? (
            <div className="empty">
              {sport === 'all' ? 'Nothing with a stream is on now or in the next few hours.' : 'Nothing listed for this sport in the next few days.'}
            </div>
          ) : null}
        </>
      )}

      {watchingItem ? (
        <Suspense fallback={null}>
          <LivePlayer item={watchingItem} onClose={() => setWatching(null)} />
        </Suspense>
      ) : null}
    </div>
  );
};

export default Live;
